import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Copy, ExternalLink } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import AuditLogPanel from "../common/AuditLogPanel";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader, TablePagination } from "../reporting/ReportingShared";
import { isMerchantScoped, isReadOnly } from "../../constants/roles";
import { successToast, errorToast } from "../../shared/Toast";
import { listPages, getPage, setPageStatus, duplicatePage, deletePage } from "../../utils/merchantPagesApi";
import {
  STATUS_META,
  PAGE_AUDIT_ACTION_LABELS,
  PAGE_AUDIT_FIELD_LABELS,
  pageAuditResolve,
  formFromPage,
} from "../../constants/merchantPages";
import PagePreviewModal from "./PagePreviewModal";
import { inputStyle, buttonStyle, btnSecondary, btnDanger, linkBtn, iconBtn, notice } from "./pageStyles";

/**
 * Pages — per-merchant public documents (privacy policy, terms, refund policy, FAQ), served at
 * {base}/{merchantSlug}/{pageSlug}.
 *
 * Merchant-scoped like every other page. Operations users see everything read-only: the axios
 * interceptor signs a user out on ANY 403, so every write is hidden here rather than left for
 * the server to refuse.
 */

const PAGE_LIMIT = 25;

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    successToast("Link copied");
  } catch (e) {
    errorToast("Could not copy — select the link and copy it by hand");
  }
};

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");
const who = (u) => (u ? [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email : null);

const PagesList = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const merchantId = isMerchantScoped(userType) ? localStorage.getItem("MerchantId") : reduxSelectedMerchant;
  const readOnly = isReadOnly(userType);
  const isAdmin = userType === "admin";
  const noMerchant = !merchantId || merchantId === "all";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, merchant: null });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [confirm, setConfirm] = useState(null); // { kind: "publish"|"unpublish"|"delete", page }
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { merchantId, page }
  const [historyOf, setHistoryOf] = useState(null);

  const load = useCallback(async () => {
    if (noMerchant) { setData({ items: [], total: 0, merchant: null }); return; }
    setLoading(true);
    const d = await listPages({
      merchantId,
      status: status === "all" ? undefined : status,
      search: search || undefined,
      page,
      limit,
    });
    setData(d);
    setLoading(false);
  }, [merchantId, noMerchant, status, search, page, limit]);

  useEffect(() => { load(); }, [load]);

  const merchant = data.merchant;
  const canPublish = Boolean(merchant?.slug);

  const applySearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); };

  const openPreview = async (p) => {
    const full = await getPage(p.id);
    if (full) setPreview({ merchantId: full.merchantId, page: formFromPage(full) });
  };

  const handleDuplicate = async (p) => {
    const copyRow = await duplicatePage(p.id);
    if (copyRow) {
      successToast("Copy created as a draft");
      navigate(`/pages/${copyRow.id}`);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const { kind, page: p } = confirm;
      if (kind === "delete") {
        if (await deletePage(p.id)) successToast("Page deleted");
      } else {
        const next = kind === "publish" ? "published" : "draft";
        if (await setPageStatus(p.id, next)) successToast(next === "published" ? "Published — the page is live" : "Unpublished");
      }
      setConfirm(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Pages" parent="Merchant" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {noMerchant ? (
            <RCard><div style={{ textAlign: "center", padding: 40, color: C.textMed }}>Select a merchant to manage its pages.</div></RCard>
          ) : (
            <>
              {merchant && !merchant.slug && (
                <div style={notice("warning")} data-testid="no-slug-notice">
                  {isAdmin ? (
                    <>
                      This merchant has no URL slug, so its pages cannot be published yet.{" "}
                      <button type="button" style={{ ...linkBtn, padding: 0, color: "inherit", textDecoration: "underline" }} onClick={() => navigate(`/merchant/edit-merchant/${merchant.id}`)}>
                        Set a URL slug in Edit Merchant
                      </button>{" "}
                      to publish pages.
                    </>
                  ) : (
                    <>This merchant has no URL slug, so its pages cannot be published yet. Ask your Wags admin to set one.</>
                  )}
                </div>
              )}

              {merchant?.indexUrl && (
                <RCard style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: C.textMed }}>Public address for {merchant.name}</div>
                      <code style={{ fontSize: 13, color: C.text, wordBreak: "break-all" }}>{merchant.indexUrl}</code>
                    </div>
                    <button type="button" style={btnSecondary} onClick={() => copy(merchant.indexUrl)}><Copy size={14} /> Copy</button>
                    <a href={merchant.indexUrl} target="_blank" rel="noopener noreferrer" style={{ ...btnSecondary, textDecoration: "none" }}><ExternalLink size={14} /> Open</a>
                  </div>
                </RCard>
              )}

              <RCard>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
                  <form onSubmit={applySearch} style={{ display: "flex", gap: 8, flex: 1, minWidth: 260 }}>
                    <input style={{ ...inputStyle, flex: 1 }} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by title or slug…" />
                    <button type="submit" style={btnSecondary}>Search</button>
                  </form>
                  <select aria-label="Status" style={{ ...inputStyle, width: "auto" }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                    <option value="all">All pages</option>
                    <option value="published">Published</option>
                    <option value="draft">Drafts</option>
                  </select>
                  {!readOnly && <button type="button" onClick={() => navigate("/pages/new")} style={buttonStyle}>＋ New page</button>}
                </div>

                {loading && data.items.length === 0 ? <Loader /> : data.items.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                    {search || status !== "all"
                      ? "No pages match these filters."
                      : "No pages yet. Start with the Privacy Policy template — app stores ask for one."}
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <th style={tbl.th}>Page</th>
                          <th style={tbl.th}>Public link</th>
                          <th style={tbl.th}>Status</th>
                          <th style={{ ...tbl.th, textAlign: "right" }}>Sections</th>
                          <th style={tbl.th}>Last updated</th>
                          <th style={tbl.th}>Edited</th>
                          <th style={tbl.th}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.items.map((p) => {
                          const live = p.status === "published";
                          const meta = STATUS_META[p.status] || STATUS_META.draft;
                          return (
                            <tr key={p.id}>
                              <td style={tbl.td}>
                                <div style={{ fontWeight: 700, color: C.text }}>{p.titleEn}</div>
                                {p.titleAr && <div dir="rtl" style={{ fontSize: 12, color: C.textMed, textAlign: "left" }}>{p.titleAr}</div>}
                              </td>
                              <td style={{ ...tbl.td, fontSize: 12 }}>
                                {live && p.publicUrl ? (
                                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <code style={{ color: C.text }}>/{merchant?.slug}/{p.slug}</code>
                                    <button type="button" style={iconBtn} onClick={() => copy(p.publicUrl)} title="Copy link" aria-label={`Copy link to ${p.titleEn}`}><Copy size={12} /></button>
                                    <a href={p.publicUrl} target="_blank" rel="noopener noreferrer" style={iconBtn} title="Open" aria-label={`Open ${p.titleEn}`}><ExternalLink size={12} /></a>
                                  </div>
                                ) : (
                                  <span style={{ color: C.textLight }}><code>/{p.slug}</code> · not public</span>
                                )}
                              </td>
                              <td style={tbl.td}><Badge color={meta.color} bg={meta.bg}>{meta.label}</Badge></td>
                              <td style={{ ...tbl.td, textAlign: "right", fontSize: 12 }}>{p.blockCount}</td>
                              <td style={{ ...tbl.td, fontSize: 12, color: C.textMed, whiteSpace: "nowrap" }}>{p.lastUpdatedOn ? fmtDate(`${p.lastUpdatedOn}T12:00:00`) : "—"}</td>
                              <td style={{ ...tbl.td, fontSize: 12, color: C.textMed, whiteSpace: "nowrap" }}>
                                {fmtDate(p.updatedAt)}
                                {who(p.updatedBy || p.createdBy) && <div style={{ fontSize: 11, color: C.textLight }}>{who(p.updatedBy || p.createdBy)}</div>}
                              </td>
                              <td style={{ ...tbl.td, whiteSpace: "nowrap", textAlign: "right" }}>
                                <button type="button" style={linkBtn} onClick={() => navigate(`/pages/${p.id}`)}>{readOnly ? "View" : "Edit"}</button>
                                <button type="button" style={linkBtn} onClick={() => openPreview(p)}>Preview</button>
                                {!readOnly && <button type="button" style={linkBtn} onClick={() => handleDuplicate(p)}>Duplicate</button>}
                                {!readOnly && (live ? (
                                  <button type="button" style={linkBtn} onClick={() => setConfirm({ kind: "unpublish", page: p })}>Unpublish</button>
                                ) : (
                                  <button
                                    type="button"
                                    style={{ ...linkBtn, color: canPublish ? C.green : C.textLight, cursor: canPublish ? "pointer" : "not-allowed" }}
                                    disabled={!canPublish}
                                    title={canPublish ? "" : "Set the merchant's URL slug first"}
                                    onClick={() => setConfirm({ kind: "publish", page: p })}
                                  >
                                    Publish
                                  </button>
                                ))}
                                {!readOnly && <button type="button" style={{ ...linkBtn, color: "#dc2626" }} onClick={() => setConfirm({ kind: "delete", page: p })}>Delete</button>}
                                <button type="button" style={linkBtn} onClick={() => setHistoryOf(p)}>History</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <TablePagination total={data.total} page={page} limit={limit} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
                  </div>
                )}
              </RCard>
            </>
          )}
        </div>
      </Container>

      <PagePreviewModal
        isOpen={Boolean(preview)}
        onClose={() => setPreview(null)}
        merchantId={preview?.merchantId}
        page={preview?.page}
      />

      <Modal isOpen={Boolean(confirm)} toggle={() => !busy && setConfirm(null)}>
        <ModalHeader toggle={() => !busy && setConfirm(null)}>
          {confirm?.kind === "publish" ? "Publish this page?" : confirm?.kind === "unpublish" ? "Unpublish this page?" : "Delete this page?"}
        </ModalHeader>
        <ModalBody style={{ color: C.textMed, lineHeight: 1.6 }}>
          {confirm?.kind === "publish" && (
            <>“<b style={{ color: C.text }}>{confirm.page.titleEn}</b>” goes live at <code>{confirm.page.publicUrl}</code>. Anyone with the link can read it.</>
          )}
          {confirm?.kind === "unpublish" && (
            <>“<b style={{ color: C.text }}>{confirm.page.titleEn}</b>” stops being public. If an App Store or Google Play listing links to it, that link will break. Browsers may keep a copy for up to 5 minutes.</>
          )}
          {confirm?.kind === "delete" && (
            <>
              “<b style={{ color: C.text }}>{confirm.page.titleEn}</b>” is deleted permanently. The last version stays in its history.
              {confirm.page.status === "published" && <div style={{ ...notice("error"), marginTop: 12, marginBottom: 0 }}>This page is live. Its public link will stop working immediately.</div>}
            </>
          )}
        </ModalBody>
        <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
          <button type="button" onClick={() => setConfirm(null)} disabled={busy} style={btnSecondary}>Back</button>
          <button type="button" onClick={runConfirm} disabled={busy} style={confirm?.kind === "delete" ? btnDanger : buttonStyle}>
            {busy ? "Working…" : confirm?.kind === "publish" ? "Yes, publish" : confirm?.kind === "unpublish" ? "Yes, unpublish" : "Yes, delete"}
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={Boolean(historyOf)} toggle={() => setHistoryOf(null)} size="lg">
        <ModalHeader toggle={() => setHistoryOf(null)}>History — {historyOf?.titleEn}</ModalHeader>
        <ModalBody>
          {historyOf && (
            <AuditLogPanel
              key={historyOf.id}
              merchantId={merchantId}
              module="pages"
              entityType="merchant_page"
              entityId={historyOf.id}
              actionLabels={PAGE_AUDIT_ACTION_LABELS}
              fieldLabels={PAGE_AUDIT_FIELD_LABELS}
              resolve={pageAuditResolve}
              title="Changes to this page"
            />
          )}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default PagesList;
